import { Text, TextProps } from '@chakra-ui/react';
import React, { ElementType, FC } from 'react';
import is from 'is';

// Chakra's `TextProps` has no `variant`, so the wrapper adds its own (h0-h6 map to the heading element,
// everything else renders as the default text).
type TypographyProps = Omit<TextProps, 'variant' | 'align'> & {
  variant?: string;
  noOfLines?: number;
  isTruncated?: boolean;
  truncated?: boolean;
  align?: TextProps['textAlign'];
  decoration?: TextProps['textDecoration'];
  casing?: TextProps['textTransform'];
  textColor?: TextProps['color'];
};

// Chakra's Text has no noOfLines/isTruncated/align/decoration/casing/textColor props, so each legacy alias
// resolves to lineClamp/truncate/textAlign/textDecoration/textTransform/color; the legacy name wins on conflict.
export const Typography: FC<TypographyProps> = ({
  variant = 'p',
  children,
  noOfLines,
  lineClamp,
  isTruncated,
  truncated,
  align,
  textAlign,
  decoration,
  textDecoration,
  casing,
  textTransform,
  textColor,
  color,
  ...rest
}) => {
  /**
   * DOM
   */
  const textProps: Omit<TextProps, 'variant'> = {
    ...rest,
    lineClamp: noOfLines ?? lineClamp,
    truncate: isTruncated ?? truncated,
    textAlign: align ?? textAlign,
    textDecoration: decoration ?? textDecoration,
    textTransform: casing ?? textTransform,
    color: textColor ?? color,
  };
  if (is.string(variant) && ['h0', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(variant as string)) {
    textProps.as = variant as ElementType;
  }
  return (
    <Text data-object-type="typography" {...textProps}>
      {children}
    </Text>
  );
};
